/**
 * Entry point for the React application.
 * 
 * This file sets up the application by importing necessary dependencies,
 * configuring the Redux store, and rendering the React application into the DOM.
 * 
 * Key Features:
 * - Uses `react-loadable` for code-splitting and lazy loading of components.
 * - Configures FontAwesome icons for use throughout the application.
 * - Sets up global CSS and SCSS styles.
 * - Implements routing with `react-router-dom` and `connected-react-router`.
 * - Supports server-side rendering with `react-frontload`.
 * - Includes custom logic for handling Firebase authentication (commented out).
 * 
 * Components:
 * - `The1Register`: Lazy-loaded component for The1 registration.
 * - `RedeemCouponRegister`: Lazy-loaded component for redeeming coupons.
 * - `CreditCardPaymentForMobile`: Lazy-loaded component for mobile credit card payments.
 * - `MainLayout`: Main layout component for the application.
 * - `RedirectPage`: Component for handling external redirects.
 * 
 * Routes:
 * - `/coupon/:event` and `/influencer/:event`: Render `RedeemCouponRegister`.
 * - `/the1/register`: Render `The1Register`.
 * - `/payment/credit/:id`: Render `CreditCardPaymentForMobile`.
 * - `/lazada`, `/shopee`, `/jd`, `/ENDYEARSALE19`: Redirect to external URLs.
 * - `/`: Render `MainLayout`.
 * 
 * Rendering:
 * - Uses `hydrate` for server-side rendered content.
 * - Falls back to `render` for client-side rendering.
 * 
 * Dependencies:
 * - React, Redux, React Router, FontAwesome, and various CSS/SCSS libraries.
 * - `react-loadable` for lazy loading.
 * - `react-frontload` for server-side rendering support.
 * 
 * Notes:
 * - Includes a polyfill for `String.prototype.replaceAll`.
 * - Firebase authentication logic is commented out but can be enabled if needed.
 */
/* eslint-disable import/first */
import 'babel-polyfill';
import React from 'react';
import { render, hydrate } from 'react-dom';
import Loadable from 'react-loadable';
import { Frontload } from 'react-frontload';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { library, config } from '@fortawesome/fontawesome-svg-core';
import { faFacebookSquare, faInstagram, faLine, faTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import {
    faBars,
    faSearch,
    faTimes,
    faCheck,
    faSyncAlt,
    faChevronLeft,
    faPlus,
    faCartPlus,
    faCartArrowDown,
    faAngleRight as farAngleRight,
    faImage as farImage,
    faEdit,
    faEye,
    faThumbsUp,
    faPlus as farPlus,
    faGiftCard,
    faEllipsisV
} from '@fortawesome/pro-regular-svg-icons';
import {
    faTruck,
    faUser,
    faShoppingBag,
    faHeart,
    faHome,
    faCreditCard,
    faStar,
    faBell,
    faSignOutAlt,
    faSearch as fasSearch,
    faEnvelope,
    faPhone,
    faCheck as fasCheck,
    faSquareFull,
    faTrashAlt,
    faPencil,
    faSortAmountDown,
    faSortAmountUp,
    faShareAlt,
    faChevronDoubleRight as fasChevronDoubleRight,
    faPaperPlane
} from '@fortawesome/pro-solid-svg-icons';
import {
    faCircle,
    faMoneyBill,
    faShippingFast,
    faBoxCheck,
    faSyncAlt as fasSyncAlt,
    faImage,
    faCaretRight,
    faPlay,
    faShoppingCart as fasShoppingCart,
    faChevronRight as fasChevronRight,
    faPencilAlt
} from '@fortawesome/pro-solid-svg-icons';
import {
    faCircle as falCircle,
    faChevronDoubleLeft,
    faChevronLeft as falChevronLeft,
    faChevronRight,
    faChevronDoubleRight,
    faChevronDown,
    faTimes as falTimes,
    faCheckCircle,
    faTimesCircle,
    faHeart as falHeart,
    faAngleLeft,
    faCalendarAlt,
    faFilm,
    faTags,
    faAddressBook,
    faComments,
    faPlus as falPlus,
    faShoppingBag as falShoppingBag,
    faUser as falUser,
    faShoppingCart as falShoppingCart,
    faStar as falStar,
    faSearch as falSearch,
    faImage as falImage,
    faSprayCan as falSprayCan,
    faEdit as falEdit,
    faFileAlt as falFileAlt,
    faCommentAltSmile,
    faBorderAll,
    faClock,
    faArrowUp,
    faArrowDown,
} from '@fortawesome/pro-light-svg-icons';

const The1Register = Loadable({
    loader: () => import(/* webpackChunkName: "The1Register"*/ './views/the1/Register'),
    loading: () => null,
    modules: ['./views/the1/Register']
});

const RedeemCouponRegister = Loadable({
    loader: () => import(/* webpackChunkName: "RedeemCouponRegister"*/ './views/RedeemCouponRegister'),
    loading: () => null,
    modules: ['./views/RedeemCouponRegister']
});

const CreditCardPaymentForMobile = Loadable({
    loader: () => import(/* webpackChunkName: "CreditCardPaymentForMobile"*/
        './views/payment/creditcard'),
    loading: () => null,
    modules: ['CreditCardPaymentForMobile']
});

//CSS Global Compulsory
import './assets/vendor/icon-line/css/simple-line-icons.css';
import './assets/loader.css';

// CSS Implementing Plugins import 'font-awesome/css/font-awesome.css'; import
// './assets/vendor/font-awesome/fontawesome-all';
import './assets/vendor/icon-line-pro/style.css';
import './assets/vendor/icon-hs/style.css';
import './assets/vendor/dzsparallaxer/dzsparallaxer.css';
import './assets/vendor/dzsparallaxer/dzsscroller/scroller.css';
import './assets/vendor/dzsparallaxer/advancedscroller/plugin.css';
import 'animate.css/animate.min.css';
import './assets/vendor/hamburgers/hamburgers.min.css';
import './assets/vendor/hs-megamenu/src/hs.megamenu.css';
import './assets/vendor/malihu-scrollbar/jquery.mCustomScrollbar.css';
import './assets/vendor/slick-carousel/slick/slick.css';
import './assets/vendor/chosen/chosen.css';
import './assets/beautrium-css/custom.scss';

import MainLayout from './MainLayout';
import createStore from './store';
import RedirectPage from './views/RedirectPage';

const { store, history } = createStore();

/* eslint-disable */
String.prototype.replaceAll = function (search, replacement) {
    var target = this;
    return target.replace(new RegExp(search, 'g'), replacement);
};
/* eslint-enable */

// const comingSoon = true;
config.searchPseudoElements = true;
library.add(faFacebookSquare, faInstagram, faLine, faTwitter, faYoutube); //fab
library.add(faBars, faSearch, faTimes, faCheck, faSyncAlt, faChevronLeft, faPlus, faCartPlus, faCartArrowDown, farAngleRight, farImage, faEdit, faEye, farPlus, faThumbsUp, faGiftCard, faEllipsisV); //far
library.add(faTruck, faUser, faShoppingBag, faHeart, faHome, faCreditCard, faStar, faBell, faSignOutAlt, fasSearch, faEnvelope, faPhone, fasCheck, faSquareFull, faTrashAlt, faPencil, faSortAmountDown, faSortAmountUp, faShareAlt, fasChevronDoubleRight, faPaperPlane); //fas
library.add(faCircle, faMoneyBill, faShippingFast, faBoxCheck, fasSyncAlt, faImage, faCaretRight, faPlay, fasShoppingCart, fasChevronRight, faPencilAlt); //fas
library.add(falCircle, faChevronDoubleLeft, falChevronLeft, faChevronRight, faChevronDoubleRight, faChevronDown, falTimes, faCheckCircle, faTimesCircle, falHeart, faAngleLeft, faCalendarAlt, faFilm, faTags, faAddressBook, faComments, falPlus, falShoppingBag, falUser, falShoppingCart, falStar, falSearch, falImage, falSprayCan, falEdit, falFileAlt, faCommentAltSmile, faBorderAll, faClock, faArrowUp, faArrowDown); //fal

const Application = (
    <Provider store={store}>
        <ConnectedRouter history={history}>
            <Frontload noServerRender={true}>
                <Switch>
                    <Route path="/coupon/:event" component={RedeemCouponRegister} />
                    <Route path="/influencer/:event" component={RedeemCouponRegister} />
                    {/* <Route path="/affiliate" component={Affiliate}/> */}
                    <Route path="/the1/register" component={The1Register} />
                    <Route path={"/payment/credit/:id"} component={CreditCardPaymentForMobile} />
                    {/* <Route path="/julypayday" component={JulyPayday} /> */}
                    {/* Redirect */}
                    <Route path="/lazada" render={(props) => <RedirectPage loc="https://www.lazada.co.th/shop/beautrium" />} />
                    <Route path="/shopee" render={(props) => <RedirectPage loc="https://shopee.co.th/beautrium_official" />} />
                    <Route path="/ENDYEARSALE19" render={(props) => <RedirectPage loc="https://www.facebook.com/beautrium/posts/1793436887455336?__tn__=-R" />} />
                    {/* End of Redirect */}
                    <Route path="/" component={MainLayout} />
                </Switch>
            </Frontload>
        </ConnectedRouter>
    </Provider>
);

const root = document.querySelector('#root');

if (root.hasChildNodes() === true) {
    // If we're running in production, we use hydrate to get fast page loads by just
    // attaching event listeners after the initial render
    Loadable
        .preloadReady()
        .then(() => {

            if (root.hasChildNodes() === true) {
                // If we're running in production, we use hydrate to get fast page loads by just
                // attaching event listeners after the initial render
                hydrate(Application, root);
            } else {
                // If we're not running on the server, just render like normal
                render(Application, root);
            }
        });
} else {
    // If we're not running on the server, just render like normal
    render(Application, root);
}